import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { ForumPost, ForumPostInsert, Profile } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import './Forum.css';

const TITLE_MAX = 200;
const BODY_MAX = 12000;

const Forum: React.FC = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setBanner(null);
    const { data, error } = await supabase
      .from('forum_posts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      const msg =
        error.code === 'PGRST116' || error.message?.includes('relation') || error.message?.includes('does not exist')
          ? 'Forum database is not set up yet. Add the forum_posts table in Supabase using supabase/forum-posts.sql.'
          : error.message || 'Could not load forum posts.';
      setBanner(msg);
      setPosts([]);
      setLoading(false);
      return;
    }

    setPosts((data as ForumPost[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void fetchPosts();
  }, [fetchPosts]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const t = title.trim();
    const b = body.trim();
    if (t.length < 3 || b.length < 10) {
      setBanner('Please enter a slightly longer title and question.');
      return;
    }
    if (t.length > TITLE_MAX || b.length > BODY_MAX) {
      setBanner(`Title must be at most ${TITLE_MAX} characters and body at most ${BODY_MAX}.`);
      return;
    }

    setSubmitting(true);
    setBanner(null);

    const { data: profileData } = await supabase.from('profiles').select('full_name').eq('user_id', user.id).maybeSingle();
    const profile = profileData as Profile | null;

    const emailLocal = user.email?.split('@')[0]?.trim();
    const authorLabel =
      (profile?.full_name && profile.full_name.trim()) || emailLocal || 'Member';

    const row: ForumPostInsert = {
      user_id: user.id,
      author_label: authorLabel.slice(0, 120),
      title: t,
      body: b
    };

    const { error } = await supabase.from('forum_posts').insert(row);

    if (error) {
      setBanner(error.message || 'Could not publish your question.');
      setSubmitting(false);
      return;
    }

    setTitle('');
    setBody('');
    setSubmitting(false);
    void fetchPosts();
  };

  return (
    <main className="forum-page">
      <div className="forum-container">
        <header className="forum-header">
          <h1 className="forum-title">Deferred MBA Community Forums</h1>
          <p className="forum-subtitle">
            Ask deferred MBA admissions questions and learn from peers building their applications alongside you.
          </p>
        </header>

        {banner && (
          <aside className="forum-banner" role="status">
            {banner}
          </aside>
        )}

        {!user ? (
          <aside className="forum-banner">
            Log in or sign up to post a question.&nbsp;
            <Link to="/login">Log in</Link>
            &nbsp;&middot;&nbsp;
            <Link to="/signup">Sign up</Link>
          </aside>
        ) : (
          <section className="forum-composer-card" aria-labelledby="forum-ask-heading">
            <h2 id="forum-ask-heading" className="forum-section-title">
              Ask a question
            </h2>
            <form onSubmit={(e) => void handleSubmit(e)}>
              <div className="forum-field">
                <label className="forum-label" htmlFor="forum-title">
                  Title
                </label>
                <input
                  id="forum-title"
                  className="forum-input"
                  maxLength={TITLE_MAX}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., How selective are deferred programs for non-target schools?"
                  autoComplete="off"
                />
              </div>
              <div className="forum-field">
                <label className="forum-label" htmlFor="forum-body">
                  Details
                </label>
                <textarea
                  id="forum-body"
                  className="forum-textarea"
                  maxLength={BODY_MAX}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Context, stats (optional), and what you’ve already considered…"
                  rows={6}
                />
              </div>
              <button type="submit" className="forum-submit" disabled={submitting}>
                {submitting ? 'Posting…' : 'Post question'}
              </button>
            </form>
          </section>
        )}

        <section aria-label="Forum questions">
          <h2 className="forum-section-title">
            Recent questions
          </h2>
          {loading ? (
            <p className="forum-empty">Loading…</p>
          ) : posts.length === 0 ? (
            <p className="forum-empty">No posts yet. Be the first to ask the community.</p>
          ) : (
            <div className="forum-feed">
              {posts.map((p) => (
                <article key={p.id} className="forum-post-card">
                  <h3 className="forum-post-title">{p.title}</h3>
                  <div className="forum-post-meta">
                    <span>{p.author_label}</span>
                    {' · '}
                    <time dateTime={p.created_at}>
                      {new Date(p.created_at).toLocaleString([], {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </time>
                  </div>
                  <p className="forum-post-body">{p.body}</p>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default Forum;
